import type {
  GroupMember,
  SearchResponse,
  User,
  UserRegistration,
} from "@fusionauth/typescript-client";
import { Lock, LockOpen, PersonAdd } from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import { Chip, Grid } from "@mui/material";
import {
  GridActionsCellItem,
  type GridColDef,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BaseTable from "../../../../components/datagrid/BaseTable.tsx";
import { TableToolbarButton } from "../../../../components/datagrid/TableToolbarButton";
import { useFetch } from "../../../../services/hooks/useFetch";
import {
  apiPrefix,
  deviceApiPrefix,
  userApiPrefix,
} from "../../../../utils/main";
import UserDetails from "./UserDetails";

export default function UserSettingsPage() {
  const addUserButton = (
    <TableToolbarButton
      startIcon={<PersonAdd />}
      onClick={() => {
        searchParams.set("mode", "create");
        setSearchParams(searchParams);
      }}
    >
      Add User
    </TableToolbarButton>
  );

  const {
    data: companies,
    fetch: fetchCompanies,
    isLoading: companiesLoading,
    error: companiesError,
  } = useFetch<Company[]>();
  const {
    data: usersData,
    fetch: fetchUsers,
    isLoading: usersLoading,
  } = useFetch<SearchResponse>();
  const {
    data: userProjectCounts,
    fetch: fetchUserProjectCounts,
    isLoading: userProjectCountsLoading,
  } = useFetch<{ [userId: string]: number }>();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState<User[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [shouldFetchUsers, setShouldFetchUsers] = useState(true);
  const [shouldFetchUserProjects, setShouldFetchUserProjects] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = searchParams.get("userId");
  let currentUser: User | undefined;
  if (rows && currentUserId) {
    currentUser = rows.find((u) => u.id === currentUserId);
  }
  const mode = searchParams.get("mode");
  const clientId =
    import.meta.env.VITE_AUTH_CLIENT_ID ||
    (window._env_ && window._env_.AUTH_CLIENT_ID);

  const userDataTableFields: GridColDef[] = [
    {
      field: "active",
      headerName: "Active",
      width: 100,
      sortable: false,
      align: "center",
      renderCell: (params) => {
        return (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.row.active ? (
              <LockOpen fontSize="small" color="success" />
            ) : (
              <Lock fontSize="small" color="error" />
            )}
          </div>
        );
      },
    },
    { field: "email", headerName: "Email", width: 200 },
    { field: "fullName", headerName: "Name", width: 150 },
    {
      field: "roles",
      headerName: "Roles",
      width: 300,
      sortable: false,
      valueGetter: (_value, row) => {
        const app = row.registrations
          ? row.registrations.find(
              (r: UserRegistration) => r.applicationId === clientId,
            )
          : null;
        return app ? app.roles : [];
      },
      renderCell: (params) => {
        const app = params.row.registrations
          ? params.row.registrations.find(
              (r: UserRegistration) => r.applicationId === clientId,
            )
          : null;
        const roles = app ? app.roles : [];
        return (
          <>
            {roles.map((role: string) => (
              <Chip key={role} label={role} size="small" sx={{ margin: 0.5 }} />
            ))}
          </>
        );
      },
    },
    {
      field: "group",
      headerName: "Company",
      width: 175,
      sortable: false,
      valueGetter: (_value, row: User) => {
        return row.memberships
          ? row.memberships.map(
              (m) => companies?.filter((c) => c.id === m.groupId)[0],
            )
          : [];
      },
      renderCell: (params) => {
        return (
          <>
            {params.row.memberships &&
              params.row.memberships.map((membership: GroupMember) => {
                const company = companies?.filter(
                  (c) => c.id === membership.groupId,
                )[0];
                return (
                  <Chip
                    key={company?.id}
                    label={company?.name}
                    size="small"
                    sx={{ margin: 0.5 }}
                  />
                );
              })}
          </>
        );
      },
    },
    {
      field: "lastLoginInstant",
      headerName: "Last Login",
      width: 150,
      valueFormatter: (value: string) => {
        const date = new Date(value);
        return date.toDateString();
      },
    },
    {
      field: "projectCount",
      headerName: "Projects",
      width: 100,
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      align: "center",
      getActions: ({ row }) => {
        return [
          // todo: placeholder for future ticket to enhance user management UI
          // <GridActionsCellItem
          //   key={row.id}
          //   icon={<Lock fontSize="small" />}
          //   label="Lock User"
          //   className="textPrimary"
          //   onClick={() => {
          //     /* empty */
          //   }}
          //   color="inherit"
          // />,
          <GridActionsCellItem
            key={row.id}
            icon={<EditIcon fontSize="small" />}
            title="Edit User"
            label="Edit User"
            className="textPrimary"
            onClick={() => {
              searchParams.set("mode", "modify");
              searchParams.set("userId", row.id);
              searchParams.set("userName", row.fullName || "");
              setSearchParams(searchParams);
            }}
            color="inherit"
          />,
          <GridActionsCellItem
            key={row.id}
            icon={<ChevronRightIcon fontSize="small" />}
            title="View User Details"
            label="View User Details"
            className="textPrimary"
            onClick={() => {
              searchParams.set("mode", "detail");
              searchParams.set("userId", row.id);
              searchParams.set("userName", row.fullName || "");
              setSearchParams(searchParams);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (companies && usersData?.users !== undefined && userProjectCounts) {
      const userData: { [key: string]: User } = {};
      usersData.users.forEach((u) => {
        if (!u.id) return;
        const app = u.registrations
          ? u.registrations.find((r) => r.applicationId === clientId)
          : null;
        const roles = app ? app.roles : [];
        if (
          !roles ||
          !(roles.includes("ADMIN") || roles.includes("CLIENT_ADMIN"))
        ) {
          u.projectCount = userProjectCounts[u.id] || 0;
        }
        userData[u.id] = u;
      });
      setRows(Object.values(userData));
      setRowCount(usersData.total || 0);
      setShouldFetchUsers(false);
    }
  }, [
    usersData,
    companies,
    paginationModel.pageSize,
    userProjectCounts,
    clientId,
  ]);

  useEffect(() => {
    if (shouldFetchUsers && !usersLoading) {
      const params = new URLSearchParams();
      params.set("page", paginationModel.page.toString());
      params.set("limit", paginationModel.pageSize.toString());
      if (sortModel.length > 0) {
        params.set(
          "sort",
          sortModel
            .map((s) => `${s.sort === "desc" ? "-" : "+"}${s.field}`)
            .join(","),
        );
      }
      if (filter !== "") {
        params.set("filter", filter);
      }

      setShouldFetchUsers(false);
      void fetchUsers(`${userApiPrefix}/user/?${params.toString()}`);
    }
  }, [
    fetchUsers,
    usersLoading,
    shouldFetchUsers,
    paginationModel,
    filter,
    sortModel,
  ]);

  useEffect(() => {
    if (!companiesLoading && !companies && !companiesError) {
      void fetchCompanies(`${deviceApiPrefix}/companies`);
    }
  }, [fetchCompanies, companies, companiesLoading, companiesError]);

  useEffect(() => {
    if (!userProjectCountsLoading && shouldFetchUserProjects) {
      void fetchUserProjectCounts(`${apiPrefix}/user-project/counts`, {
        postResponseAction: () => {
          setShouldFetchUserProjects(false);
        },
      });
    }
  }, [
    fetchUserProjectCounts,
    shouldFetchUserProjects,
    userProjectCounts,
    userProjectCountsLoading,
  ]);

  return (
    <Grid
      container
      direction="column"
      className="company-settings-item"
      sx={{ flex: 1 }}
    >
      {companies &&
      (mode === "create" || (mode !== "create" && currentUser)) ? (
        <UserDetails
          user={currentUser}
          companies={companies}
          isEdit={mode === "modify" || mode === "create"}
          triggerUserFetch={() => {
            setShouldFetchUsers(true);
            setShouldFetchUserProjects(true);
          }}
        />
      ) : (
        <div
          style={{
            height: "100%",
            minHeight: "400px",
            maxWidth: "calc(90vw - 15px)",
          }}
        >
          <BaseTable<User>
            disableColumnFilter
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model);
              setShouldFetchUsers(true);
            }}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(sortModel) => {
              setSortModel(sortModel);
              setShouldFetchUsers(true);
            }}
            filterMode="server"
            onFilterModelChange={(filterModel) => {
              setFilter(filterModel.quickFilterValues?.join(" ") || "");
              setShouldFetchUsers(true);
            }}
            rowCount={rowCount || 0}
            loading={shouldFetchUsers || usersLoading}
            slotProps={{
              toolbar: {
                showColumns: false,
                showFilter: false,
                showViewMenu: false,
                showQuickFilter: true,
                quickFilterColumns: "email or name",
                customElements: addUserButton,
              },
            }}
            columns={userDataTableFields}
            rows={rows || []}
            tableId="user-settings-data-table"
            sx={{
              "& .MuiDataGrid-cell": {
                alignContent: "center",
              },
              overflowX: "auto",
            }}
          />
        </div>
      )}
    </Grid>
  );
}
